'use client';
import { CloseIcon, HeartIcon, MenuIcon, PersonOutlineIcon } from '@c/icons';
import { NotificationButton } from '@features/notifications';
import { UserIcon } from '@heroicons/react/20/solid';
import Button from '@ui/Button';
import SearchBox from '@ui/SearchBox';
import { isLargeDesktop, isMobile } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { useSearch } from 'context/SearchContext';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';
import MenuDrawer from './drawers/MenuDrawer';
``;
import ChatButton from './icons/buttons/ChatButton';
import ShoppingCartButton from './icons/buttons/ShoppingCartButton';
import UserMenu from './menus/UserMenu';
import CategoryBar from './navigation/CategoryBar';
import { Configure, InstantSearch } from 'react-instantsearch-hooks-web';
import type { SearchClient } from 'typesense-instantsearch-adapter';
import { searchClient } from '@util/getTypesense';
import SearchResultsPreview from './SearchResultsPreview';
import { useAnimationControls } from 'framer-motion';
import { MotionDiv } from 'motion';
import MinimalLogo from './icons/logo';

const Navbar = () => {
  const { user, userDoc } = useAuth();
  const {
    showMobileSearchbar,
    setShowMobileSearchbar,
    showSearchPreview,
    setShowSearchPreview,
  } = useSearch();

  const pathname = usePathname();
  const [searchboxFocused, setSearchboxFocused] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const searchBoxController = useAnimationControls();

  useEffect(() => {
    if (showSearchPreview) {
      searchBoxController.start({ top: 0 });
    } else {
      searchBoxController.start({ top: '5.3rem' });
    }
  }, [showSearchPreview, searchBoxController]);

  return (
    <>
      <MenuDrawer show={showDrawer} dismiss={() => setShowDrawer(false)} />

      <div
        className={`${
          isMobile() && showSearchPreview ? 'fixed' : 'sticky'
        } top-0 z-[50] w-full bg-brand-white pt-8 sm:pr-[4.5rem] sm:pt-0 lg:pl-4`}
      >
        {/* Top bar */}
        <div className="top-0 z-40 mb-5 flex h-[9.5rem] w-full justify-between bg-brand-white sm:mx-auto sm:max-w-content sm:items-center lg:h-[8rem] lg:gap-[1.6rem]">
          {/* Left nav items */}
          {/* desktop */}
          <div className="mx-auto hidden max-w-content grow gap-[2.4rem] lg:flex">
            <Link href="/" className="flex items-center pt-4">
              <MinimalLogo className="h-[4.5rem] w-auto" />
            </Link>

            <Suspense
              fallback={<div className="absolute z-[90] !h-[5.5rem] w-full" />}
            >
              <div className="relative w-full">
                <SearchBox
                  onBlur={() => setSearchboxFocused(false)}
                  onFocus={() => setSearchboxFocused(true)}
                  dismissOnBlur={true}
                  className="relative z-[45] !h-[5.5rem]"
                  showSearchPreview={showSearchPreview}
                />

                {showSearchPreview && (
                  <InstantSearch searchClient={searchClient as SearchClient}>
                    <Configure
                      // @ts-ignore FIXME: not sure why hitsPerPage throws type error... see https://discourse.algolia.com/t/property-hitsperpage-does-not-exist-for-configure-hitsperpage/18231
                      hitsPerPage={isMobile() ? 3 : 5}
                    />
                    <SearchResultsPreview
                      setShowSearchPreview={setShowSearchPreview}
                    />
                  </InstantSearch>
                )}
              </div>
            </Suspense>
          </div>

          {/* mobile */}
          <div className="relative flex w-full flex-col items-center justify-between pl-[0.8rem] lg:hidden">
            <div className="flex w-full justify-between">
              <button
                type="button"
                aria-label="menu-trigger"
                onClick={() => setShowDrawer(true)}
                className="mr-[1.6rem] text-brand-black lg:hidden lg:w-fit"
              >
                {showDrawer ? <CloseIcon /> : <MenuIcon />}
              </button>
              <Link
                aria-label="link to home"
                href="/"
                className="flex items-center justify-start lg:w-1/3"
              >
                <MinimalLogo className="h-12 w-auto" />
              </Link>

              <div className="mx-[1.6rem] flex grow items-center justify-end gap-[1.6rem] lg:w-fit lg:gap-[2rem]">
                <div className="flex items-center">
                  {!user || user.isAnonymous ? (
                    <div className="flex items-center gap-x-1">
                      <Button
                        type="text"
                        width="small"
                        href={`/login?redirect=${pathname}`}
                        ariaText="login"
                        leadingIcon={<PersonOutlineIcon />}
                      />

                      <ShoppingCartButton displayCount={true} />
                    </div>
                  ) : !showMobileSearchbar ? (
                    <div className="flex items-center gap-x-[2rem]">
                      <NotificationButton />

                      <UserMenu />

                      <ShoppingCartButton displayCount={true} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <MotionDiv
              initial={{ top: '5.3rem' }}
              animate={searchBoxController}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`absolute w-full pl-2 pr-4 ${
                showSearchPreview ? 'pt-[1.6rem]' : ''
              }`}
            >
              <Suspense fallback={<div className="!h-[4.375rem] w-full" />}>
                <SearchBox
                  onMobileFocus={() => setSearchboxFocused(true)}
                  dismissOnBlur
                  onEnter={() => setShowMobileSearchbar(false)}
                  showSearchPreview={showSearchPreview}
                />
              </Suspense>

              {isMobile() && showSearchPreview && (
                <div className="fixed h-screen w-full overflow-hidden bg-white">
                  <InstantSearch searchClient={searchClient as SearchClient}>
                    <Configure
                      // @ts-ignore FIXME: not sure why hitsPerPage throws type error... see https://discourse.algolia.com/t/property-hitsperpage-does-not-exist-for-configure-hitsperpage/18231
                      hitsPerPage={isMobile() ? 3 : 5}
                    />

                    <SearchResultsPreview
                      setShowSearchPreview={setShowSearchPreview}
                    />
                  </InstantSearch>
                </div>
              )}
            </MotionDiv>
          </div>
          {/* end left nav items */}

          {/* Right nav items */}
          {(!searchboxFocused || isLargeDesktop()) && (
            <div className="flex items-center gap-[0.4rem] font-medium text-brand-black lg:gap-[1.2rem]">
              {user && !user.isAnonymous ? (
                <div className="hidden gap-[0.8rem] lg:flex">
                  <div className="flex items-center gap-x-4">
                    <Link
                      href="/dashboard/favorites"
                      className="flex min-w-[4.4rem] items-center p-4 text-brand-black transition-colors hover:text-brand-primary"
                      aria-label="/dashboard/favorites"
                    >
                      <HeartIcon className="h-[3rem] w-[3rem] sm:h-[3.7rem] sm:w-[3.7rem]" />
                    </Link>

                    <div className="relative">
                      <NotificationButton />
                    </div>

                    <ChatButton isSupport={!!userDoc?.roles?.support} />

                    <UserMenu />

                    <ShoppingCartButton displayCount={true} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="hidden items-center gap-[0.8rem] lg:flex">
                    <Button
                      href={{
                        pathname: '/signup',
                        ...(pathname !== '/' && {
                          query: {
                            redirect: pathname,
                          },
                        }),
                      }}
                      type="text"
                      text="Sign up"
                      width="smaller"
                    />
                    <Button
                      href={{
                        pathname: '/login',
                        ...(pathname !== '/' && {
                          query: {
                            redirect: pathname,
                          },
                        }),
                      }}
                      type="text"
                      text="Login"
                      width="smaller"
                    />
                    <ShoppingCartButton displayCount={true} />
                  </div>

                  <div className="lg:hidden">
                    <UserIcon />
                  </div>
                </>
              )}
            </div>
          )}

          <div className="hidden items-center justify-center font-extrabold tracking-wider lg:flex">
            <Button
              text="SELL"
              type="secondary"
              width={'normal'}
              className="!rounded-[1rem]"
              href={
                userDoc?.account_activated ? '/create-listing' : '/how-it-works'
              }
              height="small"
            />
          </div>
        </div>

        <div className="h-px w-full bg-brand-lightest-gray" />

        <CategoryBar />

        <div className="h-px w-full bg-brand-lightest-gray" />
      </div>
    </>
  );
};

export default Navbar;
